/**
 * EP 价格档位管理 — GET/POST/PATCH /api/admin/ep-pricing/tiers
 *                   + POST /api/admin/ep-pricing/bulk-replace
 *                   + GET  /api/admin/ep-pricing/active
 *
 * Admin CRUD for the `ep_price_tier` table. Tiers map CNY/USDT forex bands
 * to the USDT price per 1 EP and the USDT amount per EP trading unit used
 * by the EP P2P market. The operator-owned table shipped with 20 legacy
 * rows (see the "restore legacy 20 tiers" bulk-replace button) and is
 * retired by flipping `is_active=false`, never a hard delete, because
 * historical sell-tickets reference tier ids.
 *
 * The page exposes four interactions:
 *   1. A paginated table of tiers with an `is_active` filter
 *   2. A create modal enforcing the same invariants as the backend
 *   3. An edit modal for level/band/price/state on an existing row
 *   4. A "restore legacy 20 tiers" button that POSTs the hardcoded legacy
 *      payload to /bulk-replace (gated on ep_pricing.tier.bulk_replace)
 *   5. An "active tier lookup" panel that resolves an exchange_rate to a
 *      matching tier using /active (gated on ep_pricing.tier.list)
 */
import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Tag,
  message,
} from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { DataTable } from '../../components/DataTable';
import { api, ApiError } from '../../api/client';
import { useAuthStore } from '../../store/auth';

interface TierRow {
  id: number;
  level: number;
  cny_min: string;
  cny_max: string;
  usdt_price: string;
  usdt_amount: string;
  is_active: boolean;
  note: string | null;
  create_time: string;
  update_time: string;
}

interface TierCreateForm {
  level: number;
  cny_min: string;
  cny_max: string;
  usdt_price: string;
  usdt_amount: string;
  is_active: boolean;
  note?: string;
}

type TierPatchForm = Partial<TierCreateForm>;

/**
 * Legacy 20-row baseline documented in `ORB源石_完整模块功能报告.md` §EP交易市场配置.
 * Row 18's upper bound is corrected from the legacy typo (6.79 → 7.79).
 */
const LEGACY_TIERS: Array<{
  level: number;
  cny_min: string;
  cny_max: string;
  usdt_price: string;
  usdt_amount: string;
  is_active: boolean;
  note: string;
}> = [
  { level: 1, cny_min: '6.00', cny_max: '6.09', usdt_price: '0.9390', usdt_amount: '123.00', is_active: true, note: 'legacy tier 1' },
  { level: 2, cny_min: '6.10', cny_max: '6.19', usdt_price: '0.9310', usdt_amount: '122.00', is_active: true, note: 'legacy tier 2' },
  { level: 3, cny_min: '6.20', cny_max: '6.29', usdt_price: '0.9240', usdt_amount: '121.00', is_active: true, note: 'legacy tier 3' },
  { level: 4, cny_min: '6.30', cny_max: '6.39', usdt_price: '0.9160', usdt_amount: '120.00', is_active: true, note: 'legacy tier 4' },
  { level: 5, cny_min: '6.40', cny_max: '6.49', usdt_price: '0.9080', usdt_amount: '119.00', is_active: true, note: 'legacy tier 5' },
  { level: 6, cny_min: '6.50', cny_max: '6.59', usdt_price: '0.9010', usdt_amount: '118.00', is_active: true, note: 'legacy tier 6' },
  { level: 7, cny_min: '6.60', cny_max: '6.69', usdt_price: '0.8930', usdt_amount: '117.00', is_active: true, note: 'legacy tier 7' },
  { level: 8, cny_min: '6.70', cny_max: '6.79', usdt_price: '0.8850', usdt_amount: '116.00', is_active: true, note: 'legacy tier 8' },
  { level: 9, cny_min: '6.80', cny_max: '6.89', usdt_price: '0.8780', usdt_amount: '115.00', is_active: true, note: 'legacy tier 9' },
  { level: 10, cny_min: '6.90', cny_max: '6.99', usdt_price: '0.8700', usdt_amount: '114.00', is_active: true, note: 'legacy tier 10' },
  { level: 11, cny_min: '7.00', cny_max: '7.09', usdt_price: '0.8620', usdt_amount: '113.00', is_active: true, note: 'legacy tier 11' },
  { level: 12, cny_min: '7.10', cny_max: '7.19', usdt_price: '0.8550', usdt_amount: '112.00', is_active: true, note: 'legacy tier 12' },
  { level: 13, cny_min: '7.20', cny_max: '7.29', usdt_price: '0.8470', usdt_amount: '111.00', is_active: true, note: 'legacy tier 13' },
  { level: 14, cny_min: '7.30', cny_max: '7.39', usdt_price: '0.8400', usdt_amount: '110.00', is_active: true, note: 'legacy tier 14' },
  { level: 15, cny_min: '7.40', cny_max: '7.49', usdt_price: '0.8320', usdt_amount: '109.00', is_active: true, note: 'legacy tier 15' },
  { level: 16, cny_min: '7.50', cny_max: '7.59', usdt_price: '0.8240', usdt_amount: '108.00', is_active: true, note: 'legacy tier 16' },
  { level: 17, cny_min: '7.60', cny_max: '7.69', usdt_price: '0.8170', usdt_amount: '107.00', is_active: true, note: 'legacy tier 17' },
  { level: 18, cny_min: '7.70', cny_max: '7.79', usdt_price: '0.8090', usdt_amount: '106.00', is_active: true, note: 'legacy tier 18 (upper bound corrected from 6.79 typo)' },
  { level: 19, cny_min: '7.80', cny_max: '7.89', usdt_price: '0.8020', usdt_amount: '105.00', is_active: true, note: 'legacy tier 19' },
  { level: 20, cny_min: '7.90', cny_max: '8.00', usdt_price: '0.7940', usdt_amount: '104.00', is_active: true, note: 'legacy tier 20' },
];

export function EpPricingTiersPage() {
  const qc = useQueryClient();
  const [isActiveFilter, setIsActiveFilter] = useState<string | undefined>(undefined);
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState<TierRow | null>(null);
  const [createForm] = Form.useForm<TierCreateForm>();
  const [editForm] = Form.useForm<TierPatchForm>();
  const [lookupForm] = Form.useForm<{ exchange_rate: string }>();
  const [lookupResult, setLookupResult] = useState<TierRow | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(false);
  const hasNode = useAuthStore((s) => s.hasNode);
  const canCreate = hasNode('ep_pricing.tier.create');
  const canEdit = hasNode('ep_pricing.tier.update');
  const canBulkReplace = hasNode('ep_pricing.tier.bulk_replace');
  const canList = hasNode('ep_pricing.tier.list');

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['admin', 'ep-price-tiers'] });
  }

  async function onCreate(values: TierCreateForm) {
    try {
      await api.post('/api/admin/ep-pricing/tiers', values);
      message.success('价格档位已创建');
      setCreateOpen(false);
      createForm.resetFields();
      invalidate();
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '创建失败');
    }
  }

  async function onEdit(values: TierPatchForm) {
    if (!editRow) return;
    try {
      await api.patch(`/api/admin/ep-pricing/tiers/${editRow.id}`, values);
      message.success('已更新');
      setEditRow(null);
      invalidate();
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '更新失败');
    }
  }

  async function onBulkRestore() {
    setRestoring(true);
    try {
      const res = await api.post<TierRow[]>('/api/admin/ep-pricing/bulk-replace', {
        tiers: LEGACY_TIERS,
      });
      message.success(`已恢复 ${res.length} 条历史档位`);
      invalidate();
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '恢复失败');
    } finally {
      setRestoring(false);
    }
  }

  async function onLookup(values: { exchange_rate: string }) {
    setLookupResult(null);
    setLookupError(null);
    try {
      const res = await api.get<TierRow>('/api/admin/ep-pricing/active', {
        exchange_rate: values.exchange_rate,
      });
      setLookupResult(res);
    } catch (err) {
      setLookupError(err instanceof ApiError ? err.message : '未找到匹配的档位');
    }
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    { title: '级别', dataIndex: 'level', width: 80 },
    {
      title: 'CNY 最低',
      dataIndex: 'cny_min',
      width: 110,
      render: (v: string) => Number(v).toFixed(2),
    },
    {
      title: 'CNY 最高',
      dataIndex: 'cny_max',
      width: 110,
      render: (v: string) => Number(v).toFixed(2),
    },
    {
      title: 'USDT 单价',
      dataIndex: 'usdt_price',
      width: 120,
      render: (v: string) => Number(v).toFixed(4),
    },
    {
      title: 'USDT 单位量',
      dataIndex: 'usdt_amount',
      width: 120,
      render: (v: string) => Number(v).toFixed(2),
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      width: 90,
      render: (v: boolean) => (v ? <Tag color="green">在用</Tag> : <Tag>已停用</Tag>),
    },
    {
      title: '备注',
      dataIndex: 'note',
      ellipsis: true,
      width: 200,
      render: (v: string | null) => v ?? '—',
    },
    { title: '创建时间', dataIndex: 'create_time', width: 170 },
    {
      title: '操作',
      key: 'op',
      width: 100,
      fixed: 'right' as const,
      render: (_: unknown, row: TierRow) => {
        if (!canEdit) return '—';
        return (
          <Button
            type="link"
            size="small"
            onClick={() => {
              setEditRow(row);
              editForm.setFieldsValue({
                level: row.level,
                cny_min: row.cny_min,
                cny_max: row.cny_max,
                usdt_price: row.usdt_price,
                usdt_amount: row.usdt_amount,
                is_active: row.is_active,
                note: row.note ?? undefined,
              });
            }}
          >
            编辑
          </Button>
        );
      },
    },
  ];

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>EP 价格档位</h2>
      <Alert
        style={{ marginBottom: 12 }}
        type="info"
        showIcon
        message="档位按 CNY/USDT 汇率带映射 USDT 单价与单位量；`is_active=false` 为下架，不做硬删除以保留历史卖单引用。"
      />

      {canList && (
        <Card
          size="small"
          title="按汇率查询当前生效档位"
          style={{ marginBottom: 12 }}
        >
          <Form
            form={lookupForm}
            layout="inline"
            onFinish={onLookup}
            initialValues={{ exchange_rate: '7.15' }}
          >
            <Form.Item
              name="exchange_rate"
              label="CNY/USDT 汇率"
              rules={[{ required: true }]}
            >
              <Input placeholder="例：7.15" style={{ width: 160 }} />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit">
                查询
              </Button>
            </Form.Item>
          </Form>
          {lookupResult && (
            <Descriptions
              size="small"
              column={4}
              bordered
              style={{ marginTop: 12 }}
            >
              <Descriptions.Item label="级别">{lookupResult.level}</Descriptions.Item>
              <Descriptions.Item label="CNY 区间">
                [{lookupResult.cny_min}, {lookupResult.cny_max}]
              </Descriptions.Item>
              <Descriptions.Item label="USDT 单价">
                {lookupResult.usdt_price}
              </Descriptions.Item>
              <Descriptions.Item label="USDT 单位量">
                {lookupResult.usdt_amount}
              </Descriptions.Item>
            </Descriptions>
          )}
          {lookupError && (
            <Alert
              style={{ marginTop: 12 }}
              type="warning"
              showIcon
              message={lookupError}
            />
          )}
        </Card>
      )}

      <Space style={{ marginBottom: 12 }} wrap>
        <Select
          placeholder="状态筛选"
          style={{ width: 140 }}
          allowClear
          value={isActiveFilter}
          onChange={setIsActiveFilter}
          options={[
            { value: 'true', label: '在用' },
            { value: 'false', label: '已停用' },
          ]}
        />
        {canCreate && (
          <Button type="primary" onClick={() => setCreateOpen(true)}>
            新建档位
          </Button>
        )}
        {canBulkReplace && (
          <Popconfirm
            title="恢复为历史 20 档"
            description="将清空当前档位表并写入 legacy 20 条基线数据，不可撤销，确定吗？"
            okText="确认恢复"
            cancelText="取消"
            okButtonProps={{ danger: true, loading: restoring }}
            onConfirm={onBulkRestore}
          >
            <Button danger loading={restoring}>
              恢复为历史 20 档
            </Button>
          </Popconfirm>
        )}
      </Space>

      <DataTable<TierRow>
        queryKey={['admin', 'ep-price-tiers', isActiveFilter ?? '']}
        endpoint="/api/admin/ep-pricing/tiers"
        extraParams={{ is_active: isActiveFilter }}
        columns={columns}
        rowKey="id"
        scroll={{ x: 1200 }}
      />

      <Modal
        open={createOpen}
        title="新建价格档位"
        onCancel={() => setCreateOpen(false)}
        onOk={() => createForm.submit()}
        width={640}
        destroyOnClose
      >
        <Form
          form={createForm}
          layout="vertical"
          initialValues={{
            is_active: true,
          }}
          onFinish={onCreate}
        >
          <Form.Item
            name="level"
            label="级别 (level)"
            tooltip="1..999，全表唯一；非 PK，可插入/停用而不重新编号"
            rules={[{ required: true }]}
          >
            <InputNumber min={1} max={999} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="cny_min"
            label="CNY 最低汇率"
            tooltip="区间下界 (含)"
            rules={[{ required: true }]}
          >
            <Input placeholder="例：6.00" />
          </Form.Item>
          <Form.Item
            name="cny_max"
            label="CNY 最高汇率"
            tooltip="区间上界 (含)，须 >= 最低汇率"
            rules={[{ required: true }]}
          >
            <Input placeholder="例：6.09" />
          </Form.Item>
          <Form.Item
            name="usdt_price"
            label="USDT 单价 (每 1 EP)"
            rules={[{ required: true }]}
          >
            <Input placeholder="例：0.9390" />
          </Form.Item>
          <Form.Item
            name="usdt_amount"
            label="USDT 单位量 (每 EP 交易单位)"
            rules={[{ required: true }]}
          >
            <Input placeholder="例：123.00" />
          </Form.Item>
          <Form.Item name="is_active" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="note" label="备注">
            <Input.TextArea rows={2} maxLength={200} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={editRow !== null}
        title={`编辑档位 · level=${editRow?.level ?? ''}`}
        onCancel={() => setEditRow(null)}
        onOk={() => editForm.submit()}
        width={640}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" onFinish={onEdit}>
          <Form.Item name="level" label="级别 (level)">
            <InputNumber min={1} max={999} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="cny_min" label="CNY 最低汇率">
            <Input />
          </Form.Item>
          <Form.Item name="cny_max" label="CNY 最高汇率">
            <Input />
          </Form.Item>
          <Form.Item name="usdt_price" label="USDT 单价 (每 1 EP)">
            <Input />
          </Form.Item>
          <Form.Item name="usdt_amount" label="USDT 单位量 (每 EP 交易单位)">
            <Input />
          </Form.Item>
          <Form.Item name="is_active" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="note" label="备注">
            <Input.TextArea rows={2} maxLength={200} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
